import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/UI";
import { ExplicitFilterBar } from "@/components/common/ExplicitFilterBar";
import { MaterialStockTable } from "@/components/materials/MaterialStockTable";
import { MaterialPurchaseModal } from "@/components/materials/MaterialPurchaseModal";
import { MATERIAL_TYPES } from "@/lib/materials";
import { toQuery } from "@/lib/products";
import { apiError } from "@/lib/customers";

const INITIAL = { q: "", product_id: "", material_type: "", status: "", supplier_id: "", active: "" };
const opt = (arr) => [{ value: "", label: "Tümü" }, ...arr];

function Summary({ s }) {
  const items = [["Toplam Malzeme", s.total, "text-slate-800", "total"], ["Kritik Stok", s.critical, "text-rose-600", "critical"], ["Stok Yok", s.out, "text-rose-800", "out"]];
  return (
    <div className="flex flex-wrap gap-3 mb-4" data-testid="material-stock-summary">
      {items.map(([l, v, c, k]) => (
        <div key={k} className="px-4 py-2 rounded-xl border border-slate-200 bg-white min-w-[140px]">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div>
          <div className={`text-xl font-bold ${c}`} data-testid={`material-stock-summary-${k}`}>{v}</div>
        </div>
      ))}
    </div>
  );
}

export default function MaterialStockPage() {
  const nav = useNavigate();
  const [data, setData] = useState({ groups: [], summary: { total: 0, critical: 0, out: 0 } });
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [po, setPo] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [filters, setFilters] = useState(INITIAL);
  const load = (f = filters) => api.get(`/material-stock${toQuery(f)}`).then((r) => { setData(r); setErr(""); }).catch((e) => setErr(apiError(e, "Malzeme stoğu yüklenemedi")));
  useEffect(() => {
    load(INITIAL);
    api.get("/products").then((p) => setProducts(Array.isArray(p) ? p : [])).catch(() => {});
    api.get("/suppliers").then((s) => setSuppliers(Array.isArray(s) ? s : [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const fields = useMemo(() => [
    { key: "q", label: "Ara (ürün, kod, malzeme, tür, tedarikçi)", width: "w-72" },
    { key: "product_id", label: "Ürün", type: "select", width: "w-52", options: opt(products.map((p) => ({ value: p.id, label: `${p.name} (${p.code})` }))) },
    { key: "material_type", label: "Malzeme Türü", type: "select", width: "w-36", options: opt(MATERIAL_TYPES.map((t) => ({ value: t, label: t }))) },
    { key: "status", label: "Stok Durumu", type: "select", width: "w-40", options: opt([{ value: "critical", label: "Kritik" }, { value: "warning", label: "Yaklaşıyor" },
      { value: "ok", label: "Yeterli" }, { value: "out", label: "Stok Yok" }, { value: "negative", label: "Negatif Stok" }]) },
    { key: "supplier_id", label: "Tedarikçi", type: "select", width: "w-44", options: opt(suppliers.map((s) => ({ value: s.id, label: s.name }))) },
    { key: "active", label: "Aktiflik", type: "select", width: "w-28", options: opt([{ value: "true", label: "Aktif" }, { value: "false", label: "Pasif" }]) },
  ], [products, suppliers]);
  return (
    <div data-testid="material-stock-page">
      <PageHeader title="Malzeme Stok" description="Ürünlere bağlı ambalaj & malzeme stokları — tanımlar Ürün Detayı → Ambalaj & Malzemeler'den yapılır" />
      <Summary s={data.summary} />
      <ExplicitFilterBar fields={fields} initial={INITIAL} onApply={(f) => { setFilters(f); load(f); }}
        pdfPath="/material-stock/pdf" pdfName="Malzeme_Stok_Listesi.pdf" testid="material-stock-filter" />
      {err && <div className="mb-3 text-sm text-rose-600" data-testid="material-stock-error">{err}</div>}
      {msg && <div className="mb-3 text-sm text-emerald-700" data-testid="material-stock-msg">{msg}</div>}
      <MaterialStockTable groups={data.groups} canPurchase onGoProduct={(pid) => nav(`/products/${pid}`)} onPurchase={setPo} />
      <MaterialPurchaseModal row={po} suppliers={suppliers} onClose={() => setPo(null)}
        onDone={(r) => { setPo(null); setMsg(`Taslak satın alma oluşturuldu: ${r.po_no}`); load(); }} />
    </div>
  );
}
